import React, { useEffect, useReducer, useRef, useState } from 'react';
import { View, Text, Button, Image, ScrollView } from '@tarojs/components';
import * as Taro from '@tarojs/taro';
import grassBackground from '@/assets/sheep/grass-bg.png';
import SheepSprite from '@/components/SheepSprite';
import Playfield from '@/components/Playfield';
import { useCardMotion } from '@/components/Playfield/useCardMotion';
import Modal from '@/components/Modal';
import SettingsPanel from '@/components/SettingsPanel';
import QuizDialog from '@/components/QuizDialog';
import { getGameStats, getLevelProgress, recordWin, recordLoss, completeLevel, formatTime, getSettings, saveSettings, loadActiveRun, saveActiveRun, getModeProgress, recordModeWin } from '@/utils/storage';
import { createIdleState, effectiveElapsedMs, canRequestProp } from '@/utils/gameEngine';
import { GameSettings, PropKind, PROP_LABELS, DIFFICULTY_LABELS } from '@/data/settings';
import { PREVIEW_VERSION } from '@/data/release';
import { BOARD_PATTERNS, REFERENCE_PATTERN_NAMES, WORLD_STAGE_NOTE } from '@/data/boardPatterns';
import sound from '@/utils/sound';
import { gameReducer } from './reducer';
import styles from './index.module.scss';

const getFastest = () => {
  const modes = getModeProgress();
  const times = [...Object.values(getLevelProgress().bestTimes), ...Object.values(modes.dailyBest), ...Object.values(modes.worldBest)];
  return times.length ? formatTime(Math.min(...times)) : '—';
};

const Game: React.FC = () => {
  const [state, dispatch] = useReducer(gameReducer, undefined, () => loadActiveRun() || createIdleState());
  const stateRef = useRef(state);
  stateRef.current = state;
  const [settings, setSettings] = useState(getSettings);
  const [showSettings, setShowSettings] = useState(false);
  const [gameStats, setGameStats] = useState(getGameStats);
  const [modeProgress, setModeProgress] = useState(getModeProgress);
  const [fastest, setFastest] = useState(getFastest);
  const [effect, setEffect] = useState('');
  const effectTimer = useRef<ReturnType<typeof setTimeout>>();
  const settledRuns = useRef(new Set<string>());
  const saveWarningShown = useRef(false);
  const motion = useCardMotion(state, dispatch, settings.animationsEnabled, matched => sound.play(matched ? 'eliminate' : 'click'));

  useEffect(() => {
    const settled = state.lastSettledRun;
    if (!settled || settledRuns.current.has(settled.runId)) return;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let warned = false;
    const settle = () => {
      const ok = settled.outcome === 'win'
        ? recordWin(settled.score, settled.runId) && (settled.boardMode
          ? recordModeWin(settled.boardMode, settled.dayKey!, settled.worldStage || 1, Math.floor(settled.elapsedMs / 1000), settled.runId)
          : completeLevel(settled.level, Math.floor(settled.elapsedMs / 1000)))
        : settled.outcome === 'fail' ? recordLoss(settled.runId) : true;
      if (ok) settledRuns.current.add(settled.runId);
      else {
        if (!warned) Taro.showToast?.({ title: '记录保存失败，正在重试', icon: 'none' });
        warned = true; retry = setTimeout(settle, 3000);
      }
      setGameStats(getGameStats()); setModeProgress(getModeProgress()); setFastest(getFastest());
    };
    settle();
    return () => clearTimeout(retry);
  }, [state.lastSettledRun]);
  useEffect(() => {
    if (motion.hasPending || effect) return;
    const timer = setTimeout(() => {
      const ok = saveActiveRun(stateRef.current);
      if (!ok && !saveWarningShown.current) {
        saveWarningShown.current = true;
        Taro.showToast?.({ title: '本局保存失败，请备份游戏记录', icon: 'none' });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [state.runId, state.revision, state.phase, motion.hasPending, effect]);
  useEffect(() => {
    if (state.phase !== 'playing') return;
    const timer = setInterval(() => dispatch({ type: 'TICK', now: Date.now() }), 1000);
    return () => clearInterval(timer);
  }, [state.phase]);
  useEffect(() => {
    sound.configure(settings.soundEnabled, settings.musicEnabled);
    sound.setPlaying(state.phase === 'playing');
  }, [settings.soundEnabled, settings.musicEnabled, state.phase]);
  useEffect(() => {
    if (motion.hasPending) return;
    if (state.phase === 'won') sound.play('win');
    if (state.phase === 'failed') sound.play('lose');
  }, [state.phase, motion.hasPending]);
  useEffect(() => () => { clearTimeout(effectTimer.current); sound.stop(); }, []);
  useEffect(() => {
    if (state.phase !== 'playing') { clearTimeout(effectTimer.current); setEffect(''); }
  }, [state.phase]);

  Taro.useDidHide(() => { saveActiveRun(motion.getState()); motion.flush(); sound.stop(); dispatch({ type: 'PAUSE', now: Date.now() }); });
  Taro.useDidShow(() => {
    setSettings(getSettings());
    setGameStats(getGameStats());
    setModeProgress(getModeProgress());
    setFastest(getFastest());
    const selected = Taro.getStorageSync('selectedLevel');
    if (selected?.mode === 'daily' || selected?.mode === 'world') {
      motion.flush(); dispatch({ type: 'EXIT_HOME', now: Date.now() });
      dispatch({ type: 'START_MODE', mode: selected.mode, stage: selected.stage || 1, settings: getSettings(), now: Date.now() });
      Taro.removeStorageSync('selectedLevel'); return;
    }
    if (selected !== undefined && selected !== null) Taro.removeStorageSync('selectedLevel');
  });

  const updateSettings = (next: GameSettings) => {
    if (saveSettings(next)) setSettings(next);
    else Taro.showToast?.({ title: '设置保存失败，请重试', icon: 'none' });
  };
  const animateShuffle = () => {
    sound.play('shuffle');
    if (!settings.animationsEnabled) return;
    clearTimeout(effectTimer.current);
    setEffect('shuffle-gather');
    effectTimer.current = setTimeout(() => {
      setEffect('shuffle-deal');
      effectTimer.current = setTimeout(() => { setEffect(''); motion.refreshLayout(); }, 200);
    }, 180);
  };
  const handleProp = (kind: PropKind) => {
    if (effect) return;
    motion.flush();
    if (kind === 'shuffle' && state.props.shuffle > 0 && state.settings.quizMode !== 'always') animateShuffle();
    if (kind !== 'shuffle' || state.props.shuffle === 0 || state.settings.quizMode === 'always') sound.play('click');
    dispatch({ type: 'USE_PROP', kind, now: Date.now() });
  };
  const clickCard = motion.click;
  const inPlay = state.phase === 'playing';
  const remaining = state.cards.filter(card => card.status === 0 || card.status === 3).length;
  const headerSettings = state.phase === 'ready' ? settings : state.settings;
  const runAction = (type: 'RESTART' | 'RESUME' | 'NEXT_LEVEL' | 'EXIT_HOME', changeLayout = false) => {
    clearTimeout(effectTimer.current); setEffect('');
    motion.flush();
    dispatch({ type, now: Date.now(), runId: state.runId, settings, changeLayout });
  };

  return (
    <View className={styles.container + (!settings.animationsEnabled ? ' ' + styles.reducedMotion : '')} id='game-root' data-phase={state.phase}>
      <Image className={styles.grassBackground} src={grassBackground} mode='scaleToFill' />
      <View className={styles.grassFade} />
      {state.phase === 'ready' ? (
        <ScrollView className={styles.startScreen} scrollY>
          <View className={styles.startContent}>
            <View className={styles.startLogo}>
              <SheepSprite name='sp_logo' size={400} />
              <View className={styles.mascot}><SheepSprite name='share_game_desc' size={190} /></View>
              <Text className={styles.logoTitle}>每日挑战 · 大世界闯关</Text>
            </View>
            <View className={styles.modeCards}>
              <Button aria-label='每日一关' className={styles.modeCard} onClick={() => dispatch({ type: 'START_MODE', mode: 'daily', now: Date.now(), settings })}>
                <Text className={styles.modeCardTitle}>每日一关</Text><Text className={styles.modeCardDesc}>原版历史牌型 · 轻松配牌</Text>
              </Button>
              <Button aria-label='羊羊大世界' className={styles.modeCard + ' ' + styles.worldCard} onClick={() => dispatch({ type: 'START_MODE', mode: 'world', stage: modeProgress.worldStage, now: Date.now(), settings })}>
                <Text className={styles.modeCardTitle}>羊羊大世界</Text><Text className={styles.modeCardDesc}>第 {modeProgress.worldStage} 关 · 原版历史牌型</Text>
              </Button>
            </View>
            <View className={styles.gameStats}>
              {[['通关次数', gameStats.totalWins], ['最快用时', fastest], ['最长连胜', gameStats.longestWinStreak]].map(([label, value]) => (
                <View key={label} className={styles.statCard}><Text className={styles.statValue}>{value}</Text><Text className={styles.statLabel}>{label}</Text></View>
              ))}
            </View>
            <Text className={styles.descText}>点击未被遮挡的牌，凑齐三张消除。</Text>
            <Text className={styles.descText}>七格卡槽放满即失败，暂存牌也需要消除。</Text>
            <View className={styles.settingSummary}>
              <Text>{settings.mode === 'props' ? '道具模式' : '无道具挑战'} · {DIFFICULTY_LABELS[settings.difficulty]}</Text>
              <Text>{settings.mode === 'props' ? '移出 ' + settings.props.remove + ' · 撤回 ' + settings.props.undo + ' · 洗牌 ' + settings.props.shuffle : '所有道具与答题奖励关闭'}</Text>
              <Text>{settings.mode === 'props' ? { off: '无需答题', refill: '用完后可答题补给', always: '每次使用道具前答题' }[settings.quizMode] : '依靠规划完成挑战'}</Text>
            </View>
            <Button className={styles.settingsButton} onClick={() => setShowSettings(true)}>开局设置</Button>
            <Text className={styles.version}>预览版本 {PREVIEW_VERSION}</Text>
          </View>
        </ScrollView>
      ) : (
        <>
          <View className={styles.header}>
            <View className={styles.headerTop}>
              <View className={styles.dailyPill}><Text>{state.boardMode === 'world' ? '羊羊大世界' : '每日一关'}</Text><Text>{state.boardMode === 'world' ? '第' + state.worldStage + '关' : state.dayKey?.slice(5).replace('-', '.')}</Text></View>
              <Button aria-label='暂停游戏' className={styles.pauseButton} disabled={!inPlay}
                onClick={() => { motion.flush(); dispatch({ type: 'PAUSE', now: Date.now() }); }}
              ><View className={styles.pauseBar} /><View className={styles.pauseBar} /></Button>
            </View>
            <View className={styles.stats}>
              {settings.showRemaining && <View className={styles.statItem}><Text className={styles.statLabel}>剩余</Text><Text className={styles.statValue}>{remaining}</Text></View>}
              <View className={styles.statItem}><Text className={styles.statLabel}>用时</Text><Text className={styles.statValue}>{formatTime(Math.floor(effectiveElapsedMs(state, Date.now()) / 1000))}</Text></View>
            </View>
            <Text className={styles.modeHint}>{headerSettings.mode === 'props' ? '道具模式' : '无道具挑战'} · {DIFFICULTY_LABELS[headerSettings.difficulty]}{state.layoutId ? ' · ' + (BOARD_PATTERNS.find(pattern => pattern.id === state.layoutId)?.name || '历史牌型') : state.layoutIndex !== undefined ? ' · ' + (REFERENCE_PATTERN_NAMES[state.layoutIndex] || '参考牌型') : ''}</Text>
          </View>
          <View className={styles.content}>
            <Playfield state={state} onClick={clickCard} flights={motion.flights} onLayout={motion.refreshLayout}
              queue={motion.queue} hiddenSlotIds={motion.hiddenSlotIds} hiddenTransitionIds={motion.hiddenTransitionIds} eliminatingIds={motion.eliminatingIds} effect={effect}
            />
            <View className={styles.controls}>
              {(['remove', 'undo', 'shuffle'] as PropKind[]).map(kind => (
                <Button key={kind} aria-label={PROP_LABELS[kind]} className={styles.toolButton + (effect || !canRequestProp(state, kind) ? ' ' + styles.toolDisabled : '')}
                  disabled={!!effect || !canRequestProp(state, kind)} onClick={() => handleProp(kind)}
                >
                  <View className={styles.propIcon}><SheepSprite name={{ remove: 'Skill_ShiftOut1', undo: 'Skill_Revocation1', shuffle: 'Skill_Random1', revive: 'Skill_ShiftOut1' }[kind]} size={56} /></View>
                  <Text className={styles.propLabel}>{PROP_LABELS[kind]}{inPlay && state.props[kind] === 0 && canRequestProp(state, kind) ? ' · 答题' : ''}</Text>
                  <Text className={styles.propCount}>{state.props[kind]}</Text>
                </Button>
              ))}
            </View>
          </View>
        </>
      )}

      {state.phase === 'paused' && (
        <View className={styles.modalOverlay}>
          <View className={styles.pauseModal}>
            <Text className={styles.modalTitle}>游戏暂停</Text>
            <Text className={styles.modalDesc}>{state.boardMode === 'world' ? '可保留当前牌型重开，也可换一张图，仍挑战当前关。' : '休息一下，回来继续。'}</Text>
            <Button className={styles.primaryAction} onClick={() => runAction('RESUME')}>继续游戏</Button>
            <View className={styles.pauseActions}>
              {state.boardMode === 'world' && <Button className={styles.button} onClick={() => runAction('RESTART')}>同图重开</Button>}
              <Button className={styles.button} onClick={() => runAction('RESTART', state.boardMode === 'world')}>{state.boardMode === 'world' ? '重开换图' : '重新开始'}</Button>
              {state.boardMode !== 'world' && <Button className={styles.secondary} onClick={() => runAction('EXIT_HOME')}>返回首页</Button>}
            </View>
            {state.boardMode === 'world' && <Button className={styles.textButton} onClick={() => runAction('EXIT_HOME')}>返回首页</Button>}
            <Button className={styles.textButton} onClick={() => setShowSettings(true)}>游戏设置</Button>
            {state.boardMode === 'world' && <Text className={styles.worldNote}>{WORLD_STAGE_NOTE}</Text>}
            <Text className={styles.version}>预览版本 {PREVIEW_VERSION}</Text>
          </View>
        </View>
      )}
      {state.phase === 'failed' && !motion.hasPending && (
        <View className={styles.modalOverlay}>
          <View className={styles.pauseModal}>
            <Text className={styles.modalTitle}>卡槽满了</Text>
            <Text className={styles.modalDesc}>复活会把前3张牌移到暂存区，保留这局进度。</Text>
            {canRequestProp(state, 'revive') && <Button className={styles.primaryAction} onClick={() => handleProp('revive')}>
              {state.props.revive > 0 ? '移出3张复活 (' + state.props.revive + ')' : '答题复活'}
            </Button>}
            <View className={styles.pauseActions}>
              {state.boardMode === 'world' && <Button className={styles.button} onClick={() => runAction('RESTART')}>同图重开</Button>}
              <Button className={styles.button} onClick={() => runAction('RESTART', state.boardMode === 'world')}>{state.boardMode === 'world' ? '重开换图' : '重新开始'}</Button>
              {state.boardMode !== 'world' && <Button className={styles.secondary} onClick={() => runAction('EXIT_HOME')}>返回首页</Button>}
            </View>
            {state.boardMode === 'world' && <Button className={styles.textButton} onClick={() => runAction('EXIT_HOME')}>返回首页</Button>}
          </View>
        </View>
      )}
      {state.phase === 'won' && !motion.hasPending && (
        <View className={styles.modalOverlay}>
          <View className={styles.pauseModal}>
            <Text className={styles.modalTitle}>恭喜过关！</Text>
            <Text className={styles.finishTime}>{formatTime(Math.floor(state.elapsedMs / 1000))}</Text>
            <Text className={styles.modalDesc}>全部牌面已消除。{state.boardMode === 'world' ? '大世界第' + state.worldStage + '关完成。' : '今日挑战完成！'}</Text>
            <Button className={styles.primaryAction} onClick={() => runAction('NEXT_LEVEL')}>{state.boardMode === 'world' ? '下一关' : '再玩今日关卡'}</Button>
            <Button className={styles.textButton} onClick={() => runAction('EXIT_HOME')}>返回首页</Button>
          </View>
        </View>
      )}
      {state.phase === 'quiz' && <QuizDialog state={state} dispatch={dispatch} onShuffle={animateShuffle} />}
      {showSettings && <Modal title='游戏设置' onClose={() => setShowSettings(false)}><SettingsPanel value={settings} onChange={updateSettings} /></Modal>}
    </View>
  );
};
export default Game;
